import React, { useEffect, useLayoutEffect, useState } from "react";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import {
  Block,
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  BlockDes,
  Icon,
  Button,
  DataTable,
  DataTableBody,
  DataTableHead,
  DataTableRow,
  DataTableItem,
  PaginationComponent,
} from "../../../components/Component";
import { Modal, ModalBody, Form, Spinner } from "reactstrap";
import { useForm } from "react-hook-form";
import { useLocation } from "react-router-dom";
import { api } from "../../../utils/api";
import { toast } from "react-toastify";
import PermissionGate from "../../../components/permissions/PermissionGate";

const paginatedRows = (res) => (Array.isArray(res?.data) ? res.data : []);

/** Clés API = Ishikawa ; libellés = usage terrain (type Donesafe). */
const FISHBONE_CATEGORIES = [
  { key: "man", label: "Main-d'œuvre" },
  { key: "machine", label: "Machine / matériel" },
  { key: "method", label: "Méthode" },
  { key: "material", label: "Matière" },
  { key: "environment", label: "Environnement" },
  { key: "measurement", label: "Mesure" },
];

const HseProcessIncidentsPage = () => {
  const location = useLocation();
  const [rows, setRows] = useState([]);
  const [sites, setSites] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [fishboneDraft, setFishboneDraft] = useState({});
  const [itemPerPage] = useState(10);
  const [listSearch, setListSearch] = useState("");
  const [listPage, setListPage] = useState(1);
  const [linkedObservationId, setLinkedObservationId] = useState(null);

  const { register, handleSubmit, reset, setValue, getValues } = useForm();
  const regIncidentModalSite = register({ required: true });
  /** Site choisi dans le modal (synchro explicite : watch + select dans Modal RHF v6 est peu fiable). */
  const [createModalSiteId, setCreateModalSiteId] = useState("");
  const [siteObservations, setSiteObservations] = useState([]);
  const [siteObservationsLoading, setSiteObservationsLoading] = useState(false);
  const rootForm = useForm();
  const capaForm = useForm();
  const whyForm = useForm({ defaultValues: { why_order: 1, why_description: "", parent_id: "" } });

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.getIncidents({ per_page: 100 });
      setRows(paginatedRows(res));
    } catch (e) {
      toast.error(e.message || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    (async () => {
      try {
        const [sRes, aRes] = await Promise.all([api.getSites(), api.getAssets({ per_page: 200 }).catch(() => ({ data: [] }))]);
        setSites(sRes.data || []);
        setAssets(paginatedRows(aRes));
      } catch (e) {
        /* ignore */
      }
    })();
  }, []);

  useEffect(() => {
    const q = new URLSearchParams(location.search);
    const oid = q.get("from_observation");
    if (!oid || !/^\d+$/.test(oid)) return;
    let cancelled = false;
    (async () => {
      try {
        const raw = await api.getObservation(oid);
        const data = raw.data ?? raw;
        if (cancelled) return;
        reset({
          title: data.title ? `Suite : ${data.title}` : `Suite observation #${oid}`,
          description: data.description ? `Observation #${oid} :\n\n${data.description}` : "",
          site_id: data.site_id != null ? String(data.site_id) : "",
          severity: data.severity || "medium",
          occurred_at: "",
          asset_id: "",
          source_observation_id: String(oid),
        });
        setCreateOpen(true);
        if (!cancelled) {
          const q = new URLSearchParams(window.location.search);
          q.delete("from_observation");
          const s = q.toString();
          window.history.replaceState({}, "", `${location.pathname}${s ? `?${s}` : ""}${location.hash || ""}`);
        }
      } catch {
        if (!cancelled) toast.error("Observation introuvable ou inaccessible");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [location.search, location.pathname, location.hash, reset]);

  useLayoutEffect(() => {
    if (!createOpen) {
      setCreateModalSiteId("");
      return;
    }
    const sid = getValues("site_id");
    setCreateModalSiteId(sid != null && String(sid).trim() !== "" ? String(sid) : "");
  }, [createOpen, getValues]);

  useEffect(() => {
    if (!createOpen) {
      setSiteObservations([]);
      setSiteObservationsLoading(false);
      return;
    }
    if (!createModalSiteId) {
      setSiteObservations([]);
      setValue("source_observation_id", "");
      return;
    }
    let cancelled = false;
    (async () => {
      setSiteObservationsLoading(true);
      try {
        const res = await api.getObservations({ site_id: createModalSiteId, per_page: 200 });
        const list = paginatedRows(res);
        if (cancelled) return;
        setSiteObservations(list);
        const cur = getValues("source_observation_id");
        if (cur && !list.some((o) => String(o.id) === String(cur))) {
          setValue("source_observation_id", "");
        }
      } catch {
        if (!cancelled) setSiteObservations([]);
      } finally {
        if (!cancelled) setSiteObservationsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [createOpen, createModalSiteId, setValue, getValues]);

  useEffect(() => {
    const q = new URLSearchParams(location.search);
    const iid = q.get("open_incident");
    if (!iid || !/^\d+$/.test(iid)) return;
    let cancelled = false;
    (async () => {
      setDetailLoading(true);
      setDetail(null);
      try {
        const m = await api.getIncident(iid);
        if (!cancelled) setDetail(m);
      } catch (e) {
        if (!cancelled) toast.error(e.message || "Incident introuvable");
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
      if (!cancelled) {
        q.delete("open_incident");
        const s = q.toString();
        window.history.replaceState({}, "", `${location.pathname}${s ? `?${s}` : ""}${location.hash || ""}`);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [location.search, location.pathname, location.hash]);

  useEffect(() => {
    const q = new URLSearchParams(location.search);
    const lid = q.get("linked_observation");
    if (!lid || !/^\d+$/.test(lid)) return;
    setLinkedObservationId(lid);
    setListPage(1);
    q.delete("linked_observation");
    const s = q.toString();
    window.history.replaceState({}, "", `${location.pathname}${s ? `?${s}` : ""}${location.hash || ""}`);
  }, [location.search, location.pathname, location.hash]);

  useEffect(() => {
    setListPage(1);
  }, [listSearch, linkedObservationId]);

  const norm = (v) => String(v ?? "").toLowerCase();
  const filteredRows = rows.filter((r) => {
    if (linkedObservationId != null && String(r.source_observation_id ?? r.source_observation?.id ?? "") !== String(linkedObservationId)) {
      return false;
    }
    if (!listSearch.trim()) return true;
    const q = norm(listSearch);
    return (
      norm(r.title).includes(q) ||
      norm(r.severity).includes(q) ||
      norm(r.status).includes(q) ||
      norm(r.site?.name).includes(q) ||
      norm(r.source_observation?.title).includes(q) ||
      String(r.source_observation_id ?? r.source_observation?.id ?? "").includes(listSearch.trim()) ||
      String(r.id).includes(listSearch.trim())
    );
  });
  const listLast = listPage * itemPerPage;
  const listFirst = listLast - itemPerPage;
  const pageRows = filteredRows.slice(listFirst, listLast);

  useEffect(() => {
    if (!detail) return;
    const list = detail.fishbone_categories || detail.fishboneCategories || [];
    const init = {};
    FISHBONE_CATEGORIES.forEach(({ key }) => {
      init[key] = list.find((x) => x.category === key)?.notes ?? "";
    });
    setFishboneDraft(init);
  }, [detail]);

  const openDetail = async (id) => {
    setDetailLoading(true);
    setDetail(null);
    try {
      const m = await api.getIncident(id);
      setDetail(m);
    } catch (e) {
      toast.error(e.message || "Erreur");
    } finally {
      setDetailLoading(false);
    }
  };

  const refreshDetail = async () => {
    if (detail?.id) await openDetail(detail.id);
    load();
  };

  const onCreate = async (data) => {
    const body = {
      site_id: parseInt(data.site_id, 10),
      title: data.title,
      description: data.description,
      severity: data.severity || null,
      occurred_at: data.occurred_at || null,
    };
    if (data.asset_id) body.asset_id = parseInt(data.asset_id, 10);
    if (data.source_observation_id && String(data.source_observation_id).trim() !== "") {
      body.source_observation_id = parseInt(data.source_observation_id, 10);
    }
    try {
      await api.createIncident(body);
      toast.success("Incident signalé");
      setCreateOpen(false);
      load();
    } catch (e) {
      const msg = e.errors ? Object.values(e.errors).flat().join(", ") : e.message;
      toast.error(msg || "Erreur");
    }
  };

  return (
    <React.Fragment>
      <Head title="Incidents & RCA" />
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle page>Incidents & RCA</BlockTitle>
              <BlockDes className="text-soft">
                Déclaration, enquête, 5 Pourquoi, Ishikawa, actions correctives puis clôture — la cause, pas seulement
                l’événement.
              </BlockDes>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <Block>
          <BlockBetween className="mb-3">
            <BlockHeadContent>
              <div className="form-control-wrap" style={{ minWidth: 260 }}>
                <div className="form-icon form-icon-left">
                  <Icon name="search" />
                </div>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Rechercher un incident…"
                  value={listSearch}
                  onChange={(e) => setListSearch(e.target.value)}
                />
              </div>
              {!loading && (
                <>
                  {linkedObservationId && (
                    <div className="d-flex align-items-center gap-2 flex-wrap mt-1 mb-1">
                      <span className="badge text-bg-primary">Observation #{linkedObservationId}</span>
                      <button
                        type="button"
                        className="btn btn-xs btn-dim btn-outline-secondary"
                        onClick={() => setLinkedObservationId(null)}
                      >
                        Retirer le filtre
                      </button>
                    </div>
                  )}
                  <BlockDes className="text-soft mt-1 mb-0">
                    <p className="small mb-0">
                      {listSearch.trim() || linkedObservationId
                        ? `${filteredRows.length} incident(s) sur ${rows.length}`
                        : `${rows.length} incident(s)`}
                    </p>
                  </BlockDes>
                </>
              )}
            </BlockHeadContent>
            <BlockHeadContent>
              <ul className="nk-block-tools g-3">
                <PermissionGate permission={["Incidents - Créer"]}>
                  <li>
                    <Button
                      color="primary"
                      size="md"
                      onClick={() => {
                        reset({
                          title: "",
                          description: "",
                          site_id: "",
                          severity: "medium",
                          occurred_at: "",
                          asset_id: "",
                          source_observation_id: "",
                        });
                        setCreateOpen(true);
                      }}
                    >
                      <Icon name="plus" />
                      <span>Signaler un incident</span>
                    </Button>
                  </li>
                </PermissionGate>
              </ul>
            </BlockHeadContent>
          </BlockBetween>
          {loading ? (
            <div className="card card-stretch">
              <div className="card-inner text-center py-5">
                <Spinner />
              </div>
            </div>
          ) : filteredRows.length === 0 ? (
            <div className="card card-stretch">
              <div className="card-inner text-center py-5">
                <p className="text-soft mb-0">
                  {listSearch ? "Aucun incident ne correspond à la recherche" : "Aucun incident"}
                </p>
              </div>
            </div>
          ) : (
            <>
              <DataTable className="card-stretch">
                <DataTableBody>
                  <DataTableHead className="nk-tb-item nk-tb-head">
                    <DataTableRow size="xl">
                      <span className="sub-text">Titre</span>
                    </DataTableRow>
                    <DataTableRow size="lg">
                      <span className="sub-text">Site</span>
                    </DataTableRow>
                    <DataTableRow size="md">
                      <span className="sub-text">Gravité</span>
                    </DataTableRow>
                    <DataTableRow size="md">
                      <span className="sub-text">Statut</span>
                    </DataTableRow>
                    <DataTableRow className="nk-tb-col-tools text-end">
                      <span className="sub-text">Actions</span>
                    </DataTableRow>
                  </DataTableHead>
                  {pageRows.map((r) => (
                    <DataTableItem key={r.id}>
                      <DataTableRow size="xl">
                        <div style={{ maxWidth: 360 }}>
                          <span className="tb-lead">{r.title}</span>
                          {(r.source_observation_id || r.source_observation?.id) && (
                            <a
                              className="tb-sub small d-block mt-1 link"
                              href={`${process.env.PUBLIC_URL || ""}/observations/${r.source_observation_id || r.source_observation?.id}/view`}
                              onClick={(e) => e.stopPropagation()}
                            >
                              Observation #{r.source_observation_id || r.source_observation?.id}
                              {r.source_observation?.title ? ` — ${r.source_observation.title}` : ""}
                            </a>
                          )}
                        </div>
                      </DataTableRow>
                      <DataTableRow size="lg">
                        <span className="tb-sub">{r.site?.name || "—"}</span>
                      </DataTableRow>
                      <DataTableRow size="md">
                        <span className="tb-sub">{r.severity || "—"}</span>
                      </DataTableRow>
                      <DataTableRow size="md">
                        <span className="badge badge-dim bg-outline-info">{r.status}</span>
                      </DataTableRow>
                      <DataTableRow className="nk-tb-col-tools">
                        <ul className="nk-tb-actions gx-1">
                          <li>
                            <button
                              type="button"
                              className="btn btn-trigger btn-icon"
                              title="Ouvrir"
                              onClick={() => openDetail(r.id)}
                            >
                              <Icon name="eye" />
                            </button>
                          </li>
                        </ul>
                      </DataTableRow>
                    </DataTableItem>
                  ))}
                </DataTableBody>
              </DataTable>
              <div className="card-inner">
                <PaginationComponent
                  itemPerPage={itemPerPage}
                  totalItems={filteredRows.length}
                  paginate={setListPage}
                  currentPage={listPage}
                />
              </div>
            </>
          )}
        </Block>

        <Modal isOpen={createOpen} toggle={() => setCreateOpen(false)} className="modal-lg">
          <div className="modal-header">
            <h5 className="modal-title">Signalement d’incident</h5>
            <button type="button" className="close" onClick={() => setCreateOpen(false)}>
              <Icon name="cross" />
            </button>
          </div>
          <ModalBody>
            <Form className="is-alter" onSubmit={handleSubmit(onCreate)}>
              <div className="form-group">
                <label className="form-label">Site *</label>
                <select
                  className="form-select"
                  name="site_id"
                  ref={regIncidentModalSite.ref}
                  onBlur={regIncidentModalSite.onBlur}
                  onChange={(e) => {
                    regIncidentModalSite.onChange(e);
                    setCreateModalSiteId(e.target.value);
                  }}
                >
                  <option value="">—</option>
                  {sites.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Titre *</label>
                <input className="form-control" name="title" ref={register({ required: true })} />
              </div>
              <div className="form-group">
                <label className="form-label">Description *</label>
                <textarea className="form-control" name="description" rows={4} ref={register({ required: true })} />
              </div>
              <div className="alert alert-pro alert-primary">
                <strong>Prévention d’abord :</strong> privilégiez les <a className="link" href={`${process.env.PUBLIC_URL || ""}/observations`}>observations</a>{" "}
                (presqu’accident, terrain) et les{" "}
                <a className="link" href={`${process.env.PUBLIC_URL || ""}/hse-process/inspections`}>inspections planifiées</a>. Réservez
                l’incident formel aux événements réels ou aux exigences d’enquête.
              </div>
              <div className="row">
                <div className="col-md-6">
                  <label className="form-label">Gravité</label>
                  <select className="form-select" name="severity" ref={register}>
                    <option value="low">Faible</option>
                    <option value="medium">Moyenne</option>
                    <option value="high">Élevée</option>
                    <option value="critical">Critique</option>
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label">Date / heure</label>
                  <input type="datetime-local" className="form-control" name="occurred_at" ref={register} />
                </div>
              </div>
              <div className="form-group mt-3">
                <label className="form-label">Actif lié</label>
                <select className="form-select" name="asset_id" ref={register}>
                  <option value="">—</option>
                  {assets.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Observation liée (optionnel)</label>
                <select className="form-select" name="source_observation_id" ref={register} disabled={!createModalSiteId || siteObservationsLoading}>
                  <option value="">
                    {!createModalSiteId ? "Sélectionnez d'abord un site" : siteObservationsLoading ? "Chargement des observations..." : "—"}
                  </option>
                  {siteObservations.map((o) => (
                    <option key={o.id} value={o.id}>
                      #{o.id}
                      {o.title ? ` — ${o.title}` : ""}
                    </option>
                  ))}
                </select>
                {!createModalSiteId && <p className="text-soft small mb-0 mt-1">Sélectionnez un site pour afficher les observations de ce site.</p>}
                {createModalSiteId && !siteObservationsLoading && siteObservations.length === 0 && (
                  <p className="text-soft small mb-0 mt-1">Aucune observation enregistrée pour ce site.</p>
                )}
              </div>
              <div className="text-end">
                <Button color="primary" type="submit">
                  Enregistrer
                </Button>
              </div>
            </Form>
          </ModalBody>
        </Modal>

        <Modal
          isOpen={detail !== null || detailLoading}
          toggle={() => {
            setDetail(null);
            setDetailLoading(false);
          }}
          className="modal-lg"
        >
          <div className="modal-header">
            <h5 className="modal-title">{detail?.title || "…"}</h5>
            <button
              type="button"
              className="close"
              onClick={() => {
                setDetail(null);
                setDetailLoading(false);
              }}
            >
              <Icon name="cross" />
            </button>
          </div>
          <ModalBody>
            {detailLoading && <Spinner />}
            {detail && !detailLoading && (
              <>
                <p className="text-soft small">{detail.description}</p>
                {(detail.source_inspection_id || detail.source_inspection?.id) && (
                  <p className="small mb-2">
                    <span className="text-soft">Origine inspection : </span>
                    <a
                      className="link"
                      href={`${process.env.PUBLIC_URL || ""}/hse-process/inspections/${detail.source_inspection_id || detail.source_inspection?.id}`}
                    >
                      #{detail.source_inspection_id || detail.source_inspection?.id}
                      {detail.source_inspection?.title ? ` — ${detail.source_inspection.title}` : ""}
                    </a>
                  </p>
                )}
                {(detail.source_observation_id || detail.source_observation?.id) && (
                  <p className="small mb-2">
                    <span className="text-soft">Observation source : </span>
                    <a
                      className="link"
                      href={`${process.env.PUBLIC_URL || ""}/observations/${detail.source_observation_id || detail.source_observation?.id}/view`}
                    >
                      #{detail.source_observation_id || detail.source_observation?.id}
                      {detail.source_observation?.title ? ` — ${detail.source_observation.title}` : ""}
                    </a>
                  </p>
                )}
                <p className="text-soft small mb-3">
                  <strong>Workflow verrouillé côté serveur :</strong> signalé → démarrer l’enquête → cause racine (statut « analysé ») → CAPA
                  terminées → clôture.
                </p>
                <div className="d-flex flex-wrap gap-2 mb-3">
                  <PermissionGate permission={["Incidents - Modifier"]}>
                    <Button
                      color="light"
                      size="sm"
                      disabled={detail.status !== "reported"}
                      title={detail.status !== "reported" ? "Réservé au statut « signalé »" : ""}
                      onClick={async () => {
                        try {
                          await api.startIncidentInvestigation(detail.id, {});
                          toast.success("Enquête démarrée");
                          await refreshDetail();
                        } catch (e) {
                          toast.error(e.message || "Erreur");
                        }
                      }}
                    >
                      Démarrer enquête
                    </Button>
                  </PermissionGate>
                  <PermissionGate permission={["Incidents - Clôturer"]}>
                    <Button
                      color="primary"
                      size="sm"
                      disabled={detail.status !== "analyzed"}
                      title={detail.status !== "analyzed" ? "Finalisez la RCA (cause racine) avant clôture" : ""}
                      onClick={async () => {
                        try {
                          await api.closeIncident(detail.id);
                          toast.success("Incident clôturé");
                          await refreshDetail();
                        } catch (e) {
                          toast.error(e.message || "Erreur");
                        }
                      }}
                    >
                      Clôturer
                    </Button>
                  </PermissionGate>
                  <Button
                    color="info"
                    size="sm"
                    disabled={aiLoading}
                    onClick={async () => {
                      setAiLoading(true);
                      try {
                        const res = await api.postIncidentRcaAiSuggest(detail.id);
                        toast.success(typeof res.summary === "string" ? res.summary : "Suggestions IA reçues (voir console)");
                        console.info("RCA IA", res);
                      } catch (e) {
                        toast.error(e.message || "IA indisponible");
                      } finally {
                        setAiLoading(false);
                      }
                    }}
                  >
                    Suggestion IA (Groq)
                  </Button>
                </div>

                <h6>Causes</h6>
                <ul className="list list-sm">
                  {(detail.causes || []).map((c) => (
                    <li key={c.id}>
                      [{c.cause_type}] {c.description}
                      {c.why_order != null ? ` (pourquoi #${c.why_order})` : ""}
                    </li>
                  ))}
                </ul>
                <Form
                  className="is-alter"
                  onSubmit={rootForm.handleSubmit(async (d) => {
                    try {
                      await api.addIncidentRootCause(detail.id, { description: d.root_description });
                      rootForm.reset({ root_description: "" });
                      toast.success("Cause racine ajoutée");
                      await refreshDetail();
                    } catch (e) {
                      toast.error(e.message || "Erreur");
                    }
                  })}
                >
                  <textarea className="form-control mb-2" name="root_description" placeholder="Cause racine" ref={rootForm.register({ required: true })} rows={2} />
                  <PermissionGate permission={["Incidents - Modifier"]}>
                    <Button
                      color="primary"
                      size="sm"
                      type="submit"
                      disabled={detail.status !== "under_investigation"}
                      title={
                        detail.status !== "under_investigation"
                          ? "Démarrez d’abord l’enquête (statut « en cours d’analyse »)"
                          : ""
                      }
                    >
                      Ajouter cause racine
                    </Button>
                  </PermissionGate>
                </Form>

                <h6 className="mt-3">5 Pourquoi</h6>
                <Form
                  className="is-alter"
                  onSubmit={whyForm.handleSubmit(async (d) => {
                    try {
                      await api.addIncidentFiveWhy(detail.id, {
                        description: d.why_description,
                        why_order: parseInt(d.why_order, 10),
                        parent_id: d.parent_id ? parseInt(d.parent_id, 10) : null,
                      });
                      whyForm.reset({ why_order: 1, why_description: "", parent_id: "" });
                      toast.success("Niveau « Pourquoi » ajouté");
                      await refreshDetail();
                    } catch (e) {
                      toast.error(e.message || "Erreur");
                    }
                  })}
                >
                  <div className="row">
                    <div className="col-md-3">
                      <label className="form-label">Ordre (1–10)</label>
                      <input type="number" min={1} max={10} className="form-control" name="why_order" ref={whyForm.register({ required: true })} />
                    </div>
                    <div className="col-md-9">
                      <label className="form-label">Cause parente (ID cause existante, optionnel)</label>
                      <input className="form-control" name="parent_id" placeholder="ex. 12" ref={whyForm.register} />
                    </div>
                  </div>
                  <textarea
                    className="form-control mb-2 mt-2"
                    name="why_description"
                    placeholder="Réponse au « Pourquoi ? »"
                    ref={whyForm.register({ required: true })}
                    rows={2}
                  />
                  <PermissionGate permission={["Incidents - Modifier"]}>
                    <Button color="primary" size="sm" type="submit">
                      Ajouter un « Pourquoi »
                    </Button>
                  </PermissionGate>
                </Form>

                <h6 className="mt-3">Ishikawa (arête de poisson)</h6>
                <p className="text-soft small">Une branche par catégorie ; enregistrez après saisie.</p>
                {FISHBONE_CATEGORIES.map(({ key, label }) => (
                  <div key={key} className="form-group">
                    <label className="form-label">{label}</label>
                    <textarea
                      className="form-control form-control-sm"
                      rows={2}
                      value={fishboneDraft[key] ?? ""}
                      onChange={(e) => setFishboneDraft((prev) => ({ ...prev, [key]: e.target.value }))}
                      disabled={detail.status === "closed"}
                    />
                  </div>
                ))}
                <PermissionGate permission={["Incidents - Modifier"]}>
                  <Button
                    color="light"
                    size="sm"
                    className="mb-3"
                    disabled={detail.status === "closed"}
                    onClick={async () => {
                      try {
                        for (const { key } of FISHBONE_CATEGORIES) {
                          const notes = (fishboneDraft[key] || "").trim();
                          await api.upsertIncidentFishbone(detail.id, { category: key, notes: notes || null });
                        }
                        toast.success("Ishikawa enregistré");
                        await refreshDetail();
                      } catch (e) {
                        toast.error(e.message || "Erreur");
                      }
                    }}
                  >
                    Enregistrer Ishikawa
                  </Button>
                </PermissionGate>

                <h6 className="mt-3">CAPA</h6>
                <ul className="list list-sm">
                  {(detail.actions || []).map((x) => (
                    <li key={x.id}>
                      {x.action_type} — {x.title} — {x.status}
                    </li>
                  ))}
                </ul>
                <Form
                  className="is-alter"
                  onSubmit={capaForm.handleSubmit(async (d) => {
                    try {
                      await api.createIncidentCapa(detail.id, {
                        action_type: d.capa_type,
                        title: d.capa_title,
                        description: d.capa_desc || null,
                      });
                      capaForm.reset({ capa_type: "corrective", capa_title: "", capa_desc: "" });
                      toast.success("Action ajoutée");
                      await refreshDetail();
                    } catch (e) {
                      toast.error(e.message || "Erreur");
                    }
                  })}
                >
                  <select className="form-select mb-2" name="capa_type" ref={capaForm.register({ required: true })}>
                    <option value="corrective">Corrective</option>
                    <option value="preventive">Préventive</option>
                  </select>
                  <input className="form-control mb-2" name="capa_title" placeholder="Titre" ref={capaForm.register({ required: true })} />
                  <textarea className="form-control mb-2" name="capa_desc" placeholder="Description" ref={capaForm.register} rows={2} />
                  <PermissionGate permission={["Incidents - Modifier"]}>
                    <Button color="primary" size="sm" type="submit">
                      Ajouter CAPA
                    </Button>
                  </PermissionGate>
                </Form>
              </>
            )}
          </ModalBody>
        </Modal>
      </Content>
    </React.Fragment>
  );
};

export default HseProcessIncidentsPage;
